/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Edit3, Building2, Globe, Mail, Rocket } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent } from '../../../../components/ui/card';
import { BigHead } from '../../../../lib/core.cjs.development';
import { cn } from '../../../../lib/utils';

function ProfileCard({
  user,
  userAvatar,
  englishLevel,
  englishLevelDisplay,
  departmentName,
  currentLevel,
  onEditAvatar,
}) {
  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email || 'User';

  return (
    <Card
      className="rounded-lg bg-card text-card-foreground shadow-sm overflow-hidden"
      style={{ border: '1px solid hsl(var(--border))' }}
    >
      <CardContent className="p-0">
        {/* Background gradient */}
        <div className="h-20 bg-gradient-to-br from-primary/80 to-primary" />

        {/* Profile content */}
        <div className="px-5 pb-5">
          {/* Avatar */}
          <div className="flex justify-center -mt-12 mb-4">
            <div className="relative group">
              <div className="w-24 h-24 rounded-full bg-white border-4 border-white shadow-lg overflow-hidden">
                <BigHead {...userAvatar} />
              </div>
              <Button
                variant="secondary"
                size="icon"
                className="absolute bottom-0 right-0 h-7 w-7 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={onEditAvatar}
              >
                <Edit3 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* User Info - Centered */}
          <div className="text-center mb-5">
            <h2 className="text-xl font-bold text-foreground">{displayName}</h2>
            {currentLevel && (
              <p className="text-sm text-primary font-bold mt-1">{currentLevel.key}</p>
            )}
          </div>

          {/* Info Items */}
          <div className="space-y-3">
            {/* Email */}
            <div className={cn('flex items-center gap-3 px-3 py-2.5 rounded-lg', 'bg-muted/50')}>
              <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm text-foreground truncate">{user?.email}</span>
            </div>

            {/* Department */}
            {departmentName && (
              <div className={cn('flex items-center gap-3 px-3 py-2.5 rounded-lg', 'bg-muted/50')}>
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm text-foreground">{departmentName}</span>
              </div>
            )}

            {/* English Level */}
            {englishLevel && (
              <div className={cn('flex items-center gap-3 px-3 py-2.5 rounded-lg', 'bg-muted/50')}>
                <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold text-foreground">{englishLevel}</span>
                  {englishLevelDisplay && (
                    <span className="text-muted-foreground ml-1">({englishLevelDisplay})</span>
                  )}
                </div>
              </div>
            )}

            {/* Current Level */}
            {currentLevel && (
              <div
                className={cn('flex items-center gap-3 px-3 py-2.5 rounded-lg', 'bg-primary/10')}
              >
                <Rocket className="h-4 w-4 text-primary shrink-0" />
                <div className="text-sm">
                  <span className="text-foreground">{currentLevel.title}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

ProfileCard.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
  }),
  userAvatar: PropTypes.object,
  englishLevel: PropTypes.string,
  englishLevelDisplay: PropTypes.string,
  departmentName: PropTypes.string,
  currentLevel: PropTypes.shape({
    _id: PropTypes.string,
    key: PropTypes.string,
    title: PropTypes.string,
  }),
  onEditAvatar: PropTypes.func.isRequired,
};

export default ProfileCard;
